'use client';

import { useState, type ReactNode } from 'react';

import { showProductTab } from '@eliadmin/core/src/components/dlz/product-detail/anchors';
import { IconButton } from '@eliadmin/core/src/components/dlz/ui/icon-button';
import { ReviewCard } from '@eliadmin/core/src/components/dlz/product-detail/reviews/review-card';
import { REVIEW_COPY } from '@eliadmin/core/src/components/dlz/product-detail/reviews/review-copy';
import { ReviewItem } from '@eliadmin/core/src/components/dlz/product-detail/reviews/review-item';
import { ReviewSignInCard } from '@eliadmin/core/src/components/dlz/product-detail/reviews/review-sign-in-card';
import { ReviewSummary } from '@eliadmin/core/src/components/dlz/product-detail/reviews/review-summary';
import { ReviewToolbar } from '@eliadmin/core/src/components/dlz/product-detail/reviews/review-toolbar';
import {
  visibleReviews,
  type ReviewFilter,
  type ReviewSort,
} from '@eliadmin/core/src/components/dlz/product-detail/reviews/reviews';
import {
  useProductReviews,
  useWriteReview,
} from '@eliadmin/core/src/components/dlz/product-detail/reviews/use-product-reviews';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Skeleton } from '@eliadmin/core/src/components/ui/skeleton';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

/** Reviews shown at once in the tab and in the teaser (Figma 60137:9455 / 60137:5696). */
const PAGE_SIZE = 3;

/** „Hodnotenia“ tab: summary, filters, list, „Zobraziť ďalšie hodnotenia“. */
export function ProductReviews() {
  const { enabled, reviews, stats, isLoading, isError, refetch } = useProductReviews();
  const { status, write } = useWriteReview();
  const { t } = useTranslations();
  const [filter, setFilter] = useState<ReviewFilter>('all');
  const [sort, setSort] = useState<ReviewSort>('newest');
  const [shown, setShown] = useState(PAGE_SIZE);

  if (!enabled) return null;

  const isEmpty = !!reviews && reviews.length === 0;
  const signedOut = status === 'signed-out';
  const list = reviews ? visibleReviews(reviews, filter, sort) : [];
  const page = list.slice(0, shown);
  const remaining = list.length - page.length;

  const changeFilter = (next: ReviewFilter) => {
    setFilter(next);
    setShown(PAGE_SIZE);
  };
  const changeSort = (next: ReviewSort) => {
    setSort(next);
    setShown(PAGE_SIZE);
  };

  let content: ReactNode;
  if (isLoading) {
    content = <ReviewsSkeleton />;
  } else if (isError || !reviews || !stats) {
    content = (
      <div
        role="alert"
        className="flex flex-col items-center gap-4 rounded-[18px] bg-[#F5F6F7] p-8 text-center"
      >
        <p className="dlz-body-m text-[#4F545C]">{REVIEW_COPY.tab.loadFailed}</p>
        <Button variant="secondary" onClick={() => refetch()}>
          {REVIEW_COPY.tab.retry}
        </Button>
      </div>
    );
  } else if (isEmpty) {
    content = (
      <div className="flex flex-col items-center gap-6 rounded-[18px] bg-[#F5F6F7] p-8 text-center">
        <p className="dlz-h4 text-[#0F1012]">{t('text608')}</p>
        {signedOut ? (
          <div className="w-full max-w-[427px] text-left">
            <ReviewSignInCard />
          </div>
        ) : (
          <Button onClick={write} className="h-12 w-full md:h-[52px] md:w-auto">
            {REVIEW_COPY.tab.write}
          </Button>
        )}
      </div>
    );
  } else {
    content = (
      <div className="grid gap-6 lg:grid-cols-[427px_minmax(0,1fr)]">
        <div className="flex flex-col gap-4 md:gap-6">
          <ReviewSummary stats={stats} />
          {signedOut ? <ReviewSignInCard /> : null}
        </div>
        <div className="flex min-w-0 flex-col gap-6">
          <ReviewToolbar
            stats={stats}
            filter={filter}
            onFilter={changeFilter}
            sort={sort}
            onSort={changeSort}
          />
          <ul className="flex flex-col gap-6">
            {page.map((review, index) => (
              <li key={`${review.created_at}-${review.email}-${index}`}>
                <ReviewItem review={review} divider={remaining > 0 || index < page.length - 1} />
              </li>
            ))}
          </ul>
          {remaining > 0 ? (
            <Button
              variant="secondary"
              onClick={() => setShown(current => current + PAGE_SIZE)}
              className="h-12 w-full self-center md:h-[52px] md:w-auto"
            >
              {REVIEW_COPY.tab.showMore} (+{Math.min(PAGE_SIZE, remaining)})
            </Button>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <section
      data-section="product-reviews"
      aria-labelledby="product-reviews-title"
      aria-busy={isLoading}
      className="flex flex-col gap-6 lg:gap-10"
    >
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between md:gap-10">
        <h2 id="product-reviews-title" className="dlz-h2 text-[#0F1012]">
          {REVIEW_COPY.tab.title}
        </h2>
        {isEmpty ? null : (
          <Button
            variant="secondary"
            onClick={write}
            disabled={signedOut}
            className="h-12 w-full md:h-[52px] md:w-auto"
          >
            {REVIEW_COPY.tab.write}
          </Button>
        )}
      </div>
      {content}
    </section>
  );
}

function ReviewsSkeleton() {
  return (
    <div aria-hidden="true" className="grid gap-6 lg:grid-cols-[427px_minmax(0,1fr)]">
      <Skeleton className="h-[255px] rounded-[18px] bg-[#F5F6F7]" />
      <div className="flex flex-col gap-6">
        <Skeleton className="h-[34px] w-2/3 rounded-full bg-[#F5F6F7]" />
        {[0, 1, 2].map(row => (
          <Skeleton key={row} className="h-[140px] rounded-[18px] bg-[#F5F6F7]" />
        ))}
      </div>
    </div>
  );
}

/** „Hodnotenia zákazníkov“ band under the description (three reviews, „Všetky hodnotenia“). */
export function ProductReviewsPreview() {
  const { enabled, reviews, stats } = useProductReviews();
  const [pageIndex, setPageIndex] = useState(0);

  // No reviews (or not loaded yet): nothing to tease; the parent band collapses with `empty:hidden`.
  if (!enabled || !reviews?.length || !stats) return null;

  const newest = visibleReviews(reviews, 'all', 'newest');
  const pages = Math.ceil(newest.length / PAGE_SIZE);
  const cards = newest.slice(pageIndex * PAGE_SIZE, (pageIndex + 1) * PAGE_SIZE);

  return (
    <section
      data-section="product-reviews-preview"
      aria-labelledby="product-reviews-preview-title"
      className="flex flex-col items-center gap-6 md:gap-8"
    >
      <h2 id="product-reviews-preview-title" className="dlz-h2 text-center text-[#0F1012]">
        {REVIEW_COPY.preview.title}
      </h2>
      <div className="relative w-full">
        <ul className="grid gap-4 md:grid-cols-3 lg:grid-cols-4 lg:gap-6">
          <li className="md:col-span-3 lg:col-span-1">
            <ReviewSummary stats={stats} className="h-full" />
          </li>
          {cards.map((review, index) => (
            <li key={`${review.created_at}-${review.email}-${index}`}>
              <ReviewCard review={review} />
            </li>
          ))}
        </ul>
        {pages > 1 ? (
          // Overhangs the container edge like in the design (the container padding is 32 px from lg).
          <IconButton
            icon="caret-right"
            label={REVIEW_COPY.preview.next}
            onClick={() => setPageIndex(current => (current + 1) % pages)}
            className="absolute right-[-30px] top-1/2 hidden -translate-y-1/2 lg:inline-flex"
          />
        ) : null}
      </div>
      <Button
        variant="secondary"
        onClick={() => showProductTab('hodnotenia')}
        className="h-12 w-full md:h-[52px] md:w-auto"
      >
        {REVIEW_COPY.preview.all}
      </Button>
    </section>
  );
}
